import BalanceIcon from '@mui/icons-material/Balance';
import PieChartIcon from '@mui/icons-material/PieChart';
import ViewListIcon from '@mui/icons-material/ViewList';
import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography
} from '@mui/material';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { default as CycleTable } from '../../AppGrid/CycleTable';
import { SterilizerPendingBiModel } from '../../AppGrid/SterilizerPendingBITable';
import CustomTabs from '../../components/common/Tabs';
import consts from '../../consts';
import { useBreadcrumbs } from '../../contexts/Breadcrumbs';
import { usePrerequisites } from '../../contexts/Prerequisites';
import { useSetState } from '../../hooks';
import Connection from '../../services/Connection';
import util from '../../util';
import Graph from '../Graph';
import { Receipt } from '../cycleinfoviews';
import { dataStore } from '../../Store';
import AtlasCycleGrid from '../../AppGrid/AtlasCycleGrid';
import EndoCycleTable from '../../AppGrid/EndoCycleTable';

const specialistRole= "sterisspecialist"; 
const IncubatorCycleDetailTable = SterilizerPendingBiModel.createGridBase();
export const CycleTableComponent = () => {
	const { tags } = useSelector(storeState => storeState.auth);
	const navigate = useNavigate();
	const location = useLocation();
	const pathname = location.pathname;
	const CyclesGrid = useMemo(
		() => (location.pathname.toLowerCase() === '/endocycleslist' ? EndoCycleTable : CycleTable),
		[pathname]
	);
	const ref = useRef();
	const [{ strings }, { getLicenseType }] = usePrerequisites();
	const tapeRef = useRef();
	const [state, setState] = useSetState({ CycleTape: null, Data: null });
	const licenceType = typeof getLicenseType === 'function' ? getLicenseType() : {};
	const cyclesOnly =
		((licenceType?.catexport || licenceType?.catsync) && !licenceType?.multi) ||
		(licenceType?.catexport && licenceType?.catsync && licenceType?.products?.length === 2);
	const fromEndoCycle = location.pathname.toLocaleLowerCase() === '/endocycleslist';
	const parentFilters = fromEndoCycle
		? {
			filter: [
				{ operator: '$or', property: 'EquipmentFamilyId', value: '61f3fa6a056dc61044d087c4', type: 'string' },
				{ operator: '$or', property: 'EquipmentFamilyId', value: '64a6f2c2c1a2ee1083be1e91', type: 'string' },
				{ operator: '$or', property: 'EquipmentFamilyId', value: '65d8889995a4821583eb2dc4', type: 'string' },
				{ operator: '$not', property: 'EquipmentFamilyId', value: '65d8889995a4821583eb2dc3', type: 'string' },
				{ operator: '$not', property: 'EquipmentFamilyId', value: '61f3fa6a056dc61044d087c3', type: 'string' },
				{ operator: '$not', property: 'EventType', value: '0', type: 'string' }
			]
		}
		: { filter: [{ operator: '$not', property: 'EventType', value: '0', type: 'string' }] };
	const hiddenColumns = cyclesOnly
		? [
			'MonitoringStatus',
			'ErrorDescription',
			'BIResult',
			'AttachFile',
			'WasherCILotNumber',
			'WasherCIResultIcon',
			'STSLotNumber',
			'STSResultIcon',
			'LCSCILotNumber',
			'LCSCIResultIcon',
			'ReviewStatus',
			'CycleActionStatus',
			'ReviewComment',
			'Comment',
			'ReviewDate',
			'ApprovedOn',
			'ReviewByUser',
			'Approver',
			'EndoscopeTopName',
			'EndoscopeBottomName',
			'EndoscopeSerialNumberTop',
			'EndoscopeSerialNumberBottom',
			'EndoscopeModelTop',
			'EndoscopeModelBottom',
			'ItemRefNumberTop',
			'ItemRefNumberBottom',
			'OperatorName',
			'UnloadOperatorName',
			'LocationId'
		]
		: [];
	const handleOnSetActiveRecord = (activeRecord, options, location) => {
		const { id, record = {} } = activeRecord;
		const {
			AdvantageCycleId = '',
			AttachmentCount,
			EquipmentFamilyId,
			EndoSystemCycleId,
			SterilizerCycleId,
			SterilizerId
		} = record;
		let { EquipmentTypeId = '' } = record;
		if (EquipmentFamilyId === consts.EquipmentFamilyType.ENSPIRE3000) {
			EquipmentTypeId = EquipmentFamilyId;
		}
		if ((!cyclesOnly && licenceType.catviewer) || location?.pathname === '/endoCycleslist' || (licenceType.catexport && tags.RoleName === 'STERIS Admin')) {
			let destPath;
			switch (EquipmentTypeId) {
				case consts.EquipmentType.AER:
					destPath = `/advantagecycleinfo?id=${AdvantageCycleId}&equipmentFamilyId=${EquipmentFamilyId}&attachmentCount=${AttachmentCount}`;
					break;
				case consts.EquipmentType.CABINET:
					destPath = `/endosystemcycleinfo?id=${EndoSystemCycleId}&attachmentCount=${AttachmentCount}&equipmentFamilyId=${EquipmentFamilyId}&isEndoCabinet=1`;
					break;
				case consts.EquipmentType.LIQUIDCHEMICAL:
					destPath = `/endosystemcycleinfo?id=${EndoSystemCycleId}&attachmentCount=${AttachmentCount}&equipmentFamilyId=${EquipmentFamilyId}&isLiquidChemical=1`;
					break;
				case consts.EquipmentType.STERILIZER:
				case consts.EquipmentType.STEAMSTERILIZER:
				case consts.EquipmentType.VHPSTERILIZER:
					destPath = `/sterilizercycleinfo?id=${SterilizerCycleId}&sterilizerId=${SterilizerId}&attachmentCount=${AttachmentCount}`;
					break;
				case consts.EquipmentType.INCUBATOR:
					destPath = `/incubatorcycledetailform?id=${record?.IncubatorCycleDetailId || 0}&attachmentCount=${record?.AttachmentCount || 0
						}&cycleId=${record?.CycleId || 0}&incubatorCycleId=${record?.IncubatorCycleId || 0}&isError=${record?.BIResult === consts.BIResult.Error
						}&isFromCycle=${true}&incubatorId=${record?.IncubatorId}&returnTo=${encodeURIComponent('/cyclelist')}`;
					break;
				case consts.EquipmentFamilyType.ENSPIRE3000:
					destPath = `/enspirecycleinfo?id=${EndoSystemCycleId}&equipmentFamilyId=${EquipmentFamilyId}&equipmentId=${record.EquipmentId}&attachmentCount=${AttachmentCount}`;
					break;
				default:
					destPath = `/cycleinfo?id=${id}`;
					break;
			}
			if (destPath) {
				navigate(destPath, { state: fromEndoCycle });
			}
		}
	};

	const handleOnSelection = (id, { field, row }) => {
		//fetch equipment type id data
		if (field === 'PrintTape') {
			const { EquipmentTypeId, AttachmentCount } = row;
			const endpoint = Connection.CycleList;
			const isWasher = false;
			const isUltraSonic = false;
			const formData = new FormData();
			formData.append('action', 'load');
			formData.append('id', id);
			formData.append('attachmentCount', AttachmentCount || 0);
			formData.append('comboTypes', JSON.stringify([{ type: 'CycleActionStatus', loaded: true }]));
			setState({ loading: true });
			fetch(endpoint, { method: 'POST', body: formData })
				.then((res) => res.json())
				.then(({ data }) => {
					const CycleTape = data?.CycleTape;
					const Data = data;
					setState({ Data: Data || '' });
					setState({ CycleTape: CycleTape || '' });
				})
				.finally(() => setState({ loading: false }));
		}
	};

	const handlePrintTape = useCallback(() => {
		const { SerialNumber, CycleName, CycleCount, CycleStart } = state.Data;
		const filename = `${SerialNumber}_${CycleName.replace(/ /g, '')}_${CycleCount}_${util.customDateTimeDisplay(
			CycleStart,
			consts.MomentFormat.MonthDateYear
		)}_Print_Tape`;
		setState({ loading: true });
		tapeRef.current.download(filename, 'pdf').finally(() => setState({ loading: false }));
	});
	return (
		<>
			<Dialog open={!!state.CycleTape} sx={{ zIndex: (theme) => theme.zIndex.tooltip }}>
				<DialogTitle sx={{ display: 'flex', flexDirection: 'row', columnGap: 1 }}>
					<Typography sx={{ flex: 1 }}>{strings.CycleType}</Typography>
				</DialogTitle>

				<DialogContent>
					{state.loading && <CircularProgress size={24} />}
					{!state.loading && <Receipt content={state.CycleTape || ''} languageCode={state.Data?.CycleLanguageCode} ref={tapeRef} />}
				</DialogContent>

				<DialogActions>
					<Button variant="contained" title="Download" size="small" onClick={handlePrintTape}>
						{strings.Download}
					</Button>
					<Button size="small" variant="contained" color="warning" onClick={() => setState({ CycleTape: null })}>
						{strings.Cancel}
					</Button>
				</DialogActions>
			</Dialog>
			<CyclesGrid
				canMutateLocation={false}
				onSetActiveRecord={handleOnSetActiveRecord}
				hiddenColumns={hiddenColumns}
				tableRef={ref}
				onSelection={handleOnSelection}
				parentFilters={parentFilters}
			/>
		</>
	);
};

const IncubatorCycleDetailTableComponent = () => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const attachmentCount = parseInt(searchParams.get('attachmentCount')) || 0;
	return <IncubatorCycleDetailTable
		parentFilters={{ filter: [{ "operator": "$eq", "value": 0, "property": "IsAcknowledge", "type": "boolean" }] }}
		onOpenForm={
			({ id, record }) => {
				navigate(`/incubatorcycledetailform?id=${id}&attachmentCount=${attachmentCount}&cycleId=${record.CycleId || 0}&incubatorCycleId=${record?.IncubatorCycleId || 0}&isError=${record.BIResult === consts.BIResult.Error}&isFromCycle=${true}&returnTo=/cyclelist`);
			}
		} />;
};

const CycleList = ({ params, activeTab: activeTabProp = 0 }) => {
	const isViewerLicense = () => {
		const { auth = {} } = dataStore.getState();
		const { tags = {} } = auth;
		return tags?.LicenseAppName.includes('catviewer')
	}
	const [, setBreadcrumbs] = useBreadcrumbs();
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [{ strings }, { getLicenseType }] = usePrerequisites();
	const licenceType = getLicenseType();
	const cyclesOnly = licenceType?.catexport && !licenceType?.multi;
	const fromEndoCycle = location?.pathname === "/endoCycleslist";
	const nonViewerLicense = isViewerLicense();
	const tabName = searchParams.get('tab') || 'list' ;
	const { 
		loginData: { 
			tags: { 
				IsAtlasLicensed = false, 
				ProductType = [], 
				RoleName = '' 
			} = {} 
		} = {} 
	} = util.localData || {};
	const atlasCycle = tabName === 'atlas';
	const isAtlasLicensed = IsAtlasLicensed;
	const productTypes = ProductType;
	const roleName = RoleName;
	const isSterisSpecialist = roleName.replace(/ /g, "").toLowerCase() === specialistRole
	const allowedRole = productTypes.includes('catviewer') || (productTypes.includes('catexport') && roleName.toLowerCase() === 'steris admin');
	const allowAtlasTab = isAtlasLicensed && allowedRole && !fromEndoCycle && !isSterisSpecialist;
	const tabs = useMemo(() => {
		const tabs = (cyclesOnly || fromEndoCycle || !nonViewerLicense)
			? [
				{
					name: 'list',
					label: 'List',
					Icon: ViewListIcon,
					Component: CycleTableComponent,
					title: fromEndoCycle ? strings.EndoCycles : strings.CycleList
				}
			]
			: [
				{
					name: 'list',
					label: 'List',
					Icon: ViewListIcon,
					Component: CycleTableComponent,
					title: fromEndoCycle ? strings.EndoCycles : strings.CycleList
				},
				{
					name: 'load',
					label:'PendingBI',
					Icon: BalanceIcon,
					Component: IncubatorCycleDetailTableComponent,
					title: `${fromEndoCycle ? strings.EndoCycles : strings.CycleList} | ${strings.PendingBI}`,
					hide: isSterisSpecialist
				},
				{
					name: 'atlas',
					label: 'Atlas',
					Icon: ViewListIcon,
					Component: AtlasCycleGrid,
					title: fromEndoCycle ? strings.EndoCycles : strings.CycleList,
					onCellClick: ({ field, row }) => {
						if (field === 'LocationCycleCount' && row.WasherCycleId) {
							navigate(`/cycleinfo?id=${row.WasherCycleId.toString()}`);
						}
					},
					hide: !allowAtlasTab,
				},
				{
					name: 'statistics',
					label: 'Statistics',
					Icon: PieChartIcon,
					Component: Graph,
					title: `${fromEndoCycle ? strings.EndoCycles : strings.CycleList} | ${strings.Statistics}`,
					hide: isSterisSpecialist
				}
			];
		return tabs.filter(tab => !tab.hide);
	}, [fromEndoCycle, allowAtlasTab]);

	const tabIndex = useMemo(() => tabs.findIndex((tab) => (tab.name === tabName)), [tabs, tabName]);
	const tab = tabIndex !== -1 ? tabIndex : 0;


	const handleOnTabChange = useCallback(
		(event, i) => {
			const searchParamsObj = {}
			for (const [key, value] of searchParams.entries()) {
				searchParamsObj[key] = value;
			}
			searchParamsObj.tab = tabs[i]?.name;
			setSearchParams(searchParamsObj);
		},
		[searchParams]
	);

	useEffect(() => {
		setBreadcrumbs({ docTitle: fromEndoCycle ? strings.EndoCycles : strings.CycleList, breadcrumbs: [{ text: fromEndoCycle ? strings.EndoCycles : strings.CycleList }] });
	}, [fromEndoCycle])



	const handleOnSetActiveRecord = useCallback(
		(record, options) => {
			const { id } = record;
			let path = '';
			path = `/cycleinfo/${id}?${record.record?.EquipmentTypeId === consts.EquipmentType.WASHER ? 'isWasher=true' : 'isWasher=false'
				}&${record.record?.EquipmentTypeId === consts.EquipmentType.ULTRASONIC
					? 'isUltraSonic=true'
					: 'isUltraSonic=false'
				}&${record.record?.EquipmentTypeId === consts.EquipmentType.ENDOWASHER
					? 'isEndoWasher=true'
					: 'isEndoWasher=false'
				}`;
			if (!cyclesOnly && !atlasCycle) {
				navigate(path, { state: record });
			}
		},
		[cyclesOnly, atlasCycle]
	);

	return (
		<Grid container>
			<Grid
				size={12}
				sx={{
					flexGrow: 1
				}}				
			>
				<Grid container>
					<Grid size={12} >
						<CustomTabs
							tabs={tabs}
							tab={tab}
							onChange={handleOnTabChange}
							slotProps={{ tabComponent: { navigate, location, onSetActiveRecord: handleOnSetActiveRecord } }}
						/>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

CycleList.propType = {
	router: PropTypes.object.isRequired
};

export default CycleList;